import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/CPRO'
import type { SalarieRechercheDTO } from '@/features/api/extranet/dto/Demande/shared'
import { useEventBus } from '@/features/application/app-events'
import type { TypeQualification } from '@/features/domaine/types/opco/Demande/CPRO'
import { deepEqual, getAge, isDate, isNullOrUndefined } from '@/features/shared/tools'
import { storeToRefs } from 'pinia'
import { computed, type InjectionKey, watch } from 'vue'
import { buildDemandeActions, NOTIF_SAISIE_SALARIE_MODIFIÉE } from '../shared'
import { useDemandeCPROStore } from './useDemandeCPROStore'

type DemandeCproActions = ReturnType<typeof useDemandeCPROActions>
export const CPRO_ACTIONS: InjectionKey<DemandeCproActions> = Symbol('cpro-inject')

export function useDemandeCPROActions() {
  const store = useDemandeCPROStore()
  const { model } = storeToRefs(store)
  const { setupCustomUpdater } = store
  const eventBus = useEventBus()

  // typeDocumentTemplate: computed(() => 'Cerfa'),
  const actionsDemande = buildDemandeActions(store)

  // Type Qualification
  const updateTypeQualification = setupCustomUpdater<TypeQualification | null>((patch, value) => {
    patch.formation!.typeQualification = value
  })

  watch(
    () => model.value?.sousDispositif,
    () => {
      if (model.value?.sousDispositif === 'Prodiat') {
        updateTypeQualification('ClassificationsCcn')
      } else {
        updateTypeQualification(null)
      }
    },
  )

  const alertPlus26AnsLabel = computed(() => {
    if (isDate(model.value?.salarie?.dateNaissance) && isDate(model.value?.contrat?.dateConclusion)) {
      const age = getAge(new Date(model.value.salarie.dateNaissance), new Date(model.value.contrat.dateConclusion))
      // Si +26 ans
      if (
        age >= 26 &&
        isNullOrUndefined(model.value?.salarie?.typeMinimuSocial) === true &&
        model.value?.salarie?.inscritPoleEmploi !== true
      ) {
        return 'Si le salarié a plus de 25 ans à la date de conclusion du contrat il doit faire partie du public éligible au sens de la règlementation.'
      }
    }
    return undefined
  })

  // Utilisé par la recherche du salarié
  function créerSalariéVide(): SalarieDTO {
    return {
      source: 'Portail',
      adresse: {
        codePostal: null,
        commune: null,
        complement: null,
        mentionsSpeciales: null,
        voie: null,
      },
      beneficieReconnaissanceTravailleurHandicape: null,
      communeNaissance: null,
      csp: null,
      dateNaissance: null,
      departementNaissance: null,
      diplomeOuTitreLePlusEleveObtenu: null,
      dureeMoisPoleEmploi: null,
      email: null,
      inscritPoleEmploi: null,
      nir: null,
      nirTemporaire: null,
      nom: null,
      nomNaissance: null,
      numeroInscriptionPoleEmploi: null,
      prenom: null,
      sexe: null,
      signature: null,
      situationAvantContrat: null,
      telephone: null,
      typeMinimuSocial: null,
      extensionBOE: null,
      equivalenceJeune: null,
    }
  }

  const patchRemplacerSalarié = store.setupCustomUpdater<SalarieRechercheDTO>((patch, salarieRecherché) => {
    // oxlint-disable-next-line no-unused-vars
    const { natureContrat, niveauFormation, siren, nic, ...salarieRecherchéClean } = salarieRecherché
    const salarie = {
      ...créerSalariéVide(),
      ...salarieRecherchéClean,
    }
    patch.salarie = salarie
  })

  // PRT-589 - modifie origine Nir
  const patchSourceSalariéToPortail = store.setupCustomUpdater<{ resetNir: boolean }>((patch, payload) => {
    if (!patch.salarie) return
    patch.salarie.source = 'Portail'
    patch.salarie.signature = null
    if (payload.resetNir) {
      patch.salarie.nir = null
      /* Notifie utilisateur */
      eventBus?.dispatch(NOTIF_SAISIE_SALARIE_MODIFIÉE)
    }
  })

  watch(
    () => ({
      nir: model.value?.salarie?.nir,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: false,
      })
    },
  )

  watch(
    () => ({
      nomNaissance: model.value?.salarie?.nomNaissance,
      prenom: model.value?.salarie?.prenom,
      sexe: model.value?.salarie?.sexe,
      dateNaissance: model.value?.salarie?.dateNaissance,
      nirTemporaire: model.value?.salarie?.nirTemporaire,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: true,
      })
    },
  )

  return {
    ...actionsDemande,
    alertPlus26AnsLabel,
    patchRemplacerSalarié,
  }
}
